import { useState, useRef, useCallback, useEffect, createRef } from 'react';

const SCROLL_DURATION = 1200;

const easeInOutCubic = (t, b, c, d) => {
  t /= d / 2;
  if (t < 1) return (c / 2) * t * t * t + b;
  t -= 2;
  return (c / 2) * (t * t * t + 2) + b;
};

const useSmoothScroll = (sections, scrollDelayTime = 1200) => {
  const [currentSection, setCurrentSection] = useState(0);
  const [prevSection, setPrevSection] = useState(null);

  const lastScrollTimeRef = useRef(0);
  const sectionRefs = useRef([]);
  sectionRefs.current = sections.map(
    (_, i) => sectionRefs.current[i] || createRef()
  );
  const touchStartY = useRef(0);
  const isScrollingRef = useRef(false);
  const [isScrollEnabled, setIsScrollEnabled] = useState(true);

  const smoothScrollTo = useCallback((element, duration) => {
    const startY = window.scrollY;
    const endY = element.getBoundingClientRect().top;
    let startTime = null;

    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = timestamp - startTime;
      const position = easeInOutCubic(progress, startY, endY, duration);
      window.scrollTo(0, position);
      if (progress < duration) {
        window.requestAnimationFrame(step);
      } else {
        window.scrollTo(0, startY + endY);
      }
    };

    window.requestAnimationFrame(step);
  }, []);

  const scrollToSection = useCallback(
    (sectionIndex, forceScroll = false) => {
      if (!forceScroll && isScrollingRef.current) return;
      const targetEl = sectionRefs.current[sectionIndex]?.current;
      if (targetEl) {
        isScrollingRef.current = true;
        smoothScrollTo(targetEl, SCROLL_DURATION);
        setPrevSection(currentSection);
        setCurrentSection(sectionIndex);
        setTimeout(() => {
          isScrollingRef.current = false;
        }, scrollDelayTime);
      }
    },
    [smoothScrollTo, scrollDelayTime, currentSection]
  );

  const setCurrentSectionExternally = useCallback(
    (sectionIndex) => {
      scrollToSection(sectionIndex, true);
    },
    [scrollToSection]
  );

  const handleEvent = useCallback(
    (event) => {
      if (!isScrollEnabled || window.isHoveringCalendar) return;

      // if (event.cancelable) event.preventDefault();

      const now = Date.now();
      if (now - lastScrollTimeRef.current < scrollDelayTime) return;
      lastScrollTimeRef.current = now;

      let direction = 0;
      if (event.type === 'wheel') {
        direction = event.deltaY > 0 ? 1 : -1;
      } else if (event.type === 'keydown') {
        if (event.key === 'ArrowDown') direction = 1;
        if (event.key === 'ArrowUp') direction = -1;
      }

      const nextIndex = Math.min(
        Math.max(currentSection + direction, 0),
        sections.length - 1
      );
      scrollToSection(nextIndex);
    },
    [
      isScrollEnabled,
      currentSection,
      sections.length,
      scrollToSection,
      scrollDelayTime,
    ]
  );

  const handleTouchStart = useCallback((event) => {
    touchStartY.current = event.touches[0].clientY;
  }, []);

  const handleTouchMove = useCallback(
    (event) => {
      if (!isScrollEnabled) return;
      const touchEndY = event.changedTouches[0].clientY;
      const touchDistance = touchStartY.current - touchEndY;
      const touchThreshold = 50;

      if (Math.abs(touchDistance) < touchThreshold) return;

      if (event.cancelable) {
        event.preventDefault();
        event.stopPropagation();
      }

      const direction = touchDistance > 0 ? 1 : -1;
      const nextIndex = Math.min(
        Math.max(currentSection + direction, 0),
        sections.length - 1
      );
      if (nextIndex !== currentSection) {
        scrollToSection(nextIndex);
      }
    },
    [isScrollEnabled, currentSection, sections.length, scrollToSection]
  );

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;

    const prev = {
      bodyOverflow: body.style.overflow,
      bodyTouchAction: body.style.touchAction,
      bodyOverscroll: body.style.overscrollBehavior,
    };

    body.style.overflow = 'hidden';
    body.style.touchAction = 'none';
    html.style.overscrollBehavior = 'none';

    const handleKeyDown = (event) => {
      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        handleEvent(event);
      }
    };

    window.addEventListener('wheel', handleEvent, { passive: false });
    window.addEventListener('touchstart', handleTouchStart, { passive: false });
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      body.style.overflow = prev.bodyOverflow;
      body.style.touchAction = prev.bodyTouchAction;
      body.style.overscrollBehavior = prev.bodyOverscroll;

      window.removeEventListener('wheel', handleEvent);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleEvent, handleTouchStart, handleTouchMove]);

  const enableScroll = useCallback(() => setIsScrollEnabled(true), []);
  const disableScroll = useCallback(() => setIsScrollEnabled(false), []);

  return {
    currentSection,
    prevSection,
    sectionRefs: sectionRefs.current,
    scrollToSection,
    setCurrentSectionExternally,
    handleEvent,
    handleTouchStart,
    handleTouchMove,
    enableScroll,
    disableScroll,
  };
};

export default useSmoothScroll;
